import { createTheme } from "@mui/material";
import colors from "./Colors";
import { tokens } from "./Tokens";
import { muiSwitchStyle } from "./switchStyle";
import { typography } from "./typography";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    label: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    label: true;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    alert: true;
    primary: true;
    secondary: true;
    secondaryWhite: true;
    primaryWhite: true;
  }
}

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 375,
      sm: 768,
      md: 1024,
      lg: 1440,
      xl: 1980
    }
  },
  palette: {
    background: {
      default: colors.Neutral.Neutral50
    },
    text: {
      primary: "#64748B"
    },
    primary: {
      main: colors.primaryColor,
      contrastText: colors.Neutral.Neutral0
    },
    secondary: {
      main: colors.secondaryColor,
      contrastText: colors.primaryColor
    },
    warning: {
      main: colors.Red.Red500
    }
  },

  typography,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "600",
          borderRadius: "200px",
          textTransform: "none"
        }
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: colors.primaryColor,
            color: colors.Neutral.Neutral0,
            border: `1px solid ${colors.primaryColor}`,
            "&:hover": {
              color: colors.Blue.Blue500,
              ".icon-svg": {
                stroke: colors.primaryColor
              }
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.General.LightColor}`
            },
            "&:active": {
              border: `2px solid ${colors.primaryColor}`,
              color: colors.Blue.Blue500,
              ".icon-svg": {
                stroke: colors.primaryColor
              }
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: colors.Neutral.Neutral0
            }
          }
        },
        {
          props: { variant: "primaryWhite" },
          style: {
            color: tokens.Text.General.Color,
            backgroundColor: colors.Neutral.Neutral0,
            ".icon-svg": {
              stroke: colors.primaryColor
            },
            "&:hover": {
              border: `1px solid ${colors.Neutral.Neutral0}`,
              backgroundColor: "transparent",
              color: colors.Neutral.Neutral0,
              ".icon-svg": {
                stroke: colors.Neutral.Neutral0
              }
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.General.LightColor}`
            },
            "&:active": {
              border: `2px solid ${tokens.Border.General.Inverse}`,
              color: colors.Neutral.Neutral0,
              ".icon-svg": {
                stroke: colors.Neutral.Neutral0
              }
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: tokens.Border.General.Color
            }
          }
        },
        {
          props: { variant: "secondary" },
          style: {
            border: `1px solid ${colors.primaryColor}`,
            color: colors.Blue.Blue500,
            "&:hover": {
              backgroundColor: colors.primaryColor,
              color: colors.Neutral.Neutral0,
              ".icon-svg": {
                stroke: colors.Neutral.Neutral0
              }
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.General.LightColor}`
            },
            "&:active": {
              backgroundColor: colors.primaryColor,
              color: colors.Neutral.Neutral0,
              ".icon-svg": {
                stroke: colors.Neutral.Neutral0
              }
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: colors.Blue.Blue500
            }
          }
        },
        {
          props: { variant: "secondaryWhite" },
          style: {
            color: colors.Neutral.Neutral0,
            border: `1px solid ${colors.Neutral.Neutral0}`,
            ".icon-svg": {
              stroke: colors.Neutral.Neutral0
            },
            "&:hover": {
              border: `1px solid ${colors.Neutral.Neutral0}`,
              backgroundColor: colors.Neutral.Neutral0,
              color: colors.primaryColor,
              ".icon-svg": {
                stroke: colors.primaryColor
              }
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.General.Inverse}`
            },
            "&:active": {
              border: `1px solid ${tokens.Border.General.LightColor}`,
              backgroundColor: colors.Neutral.Neutral0,
              color: colors.primaryColor,
              ".icon-svg": {
                stroke: colors.primaryColor
              }
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: colors.Neutral.Neutral0
            }
          }
        },
        {
          props: { variant: "alert" },
          style: {
            backgroundColor: colors.Red.Red100,
            color: colors.Red.Red500,
            ".icon-svg": {
              stroke: colors.Red.Red500
            },
            "&:hover": {
              backgroundColor: colors.Neutral.Neutral0,
              border: `1px solid ${colors.Red.Red500}`
            },
            "&:focus": {
              border: `2px solid ${tokens.Border.Feedback.LightAlert}`
            },
            "&:active": {
              backgroundColor: colors.Neutral.Neutral0,
              border: `1px solid ${colors.Red.Red500}`
            },
            "&.Mui-disabled": {
              opacity: 0.35,
              color: colors.Red.Red500
            }
          }
        },
        {
          props: { variant: "text" },
          style: {
            margin: "15px 0",
            padding: "0",
            border: "0"
          }
        }
      ]
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          height: "34px",
          width: "34px",
          padding: "8px",
          color: tokens.Icon.General.Secondary,
          "&:hover": {
            background: tokens.Icon.General.Inverse,
            color: tokens.Icon.General.Secondary
          },
          "&:active": {
            border: `2px solid ${tokens.Border.General.Secondary}`,
            color: tokens.Icon.General.Secondary
          },
          "&:focus": {
            border: `2px solid ${tokens.Border.General.Secondary}`,
            background: tokens.Icon.General.Inverse,
            color: tokens.Icon.General.Secondary
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiPaper-root": {
            "& > .MuiGrid-root": {
              overflow: "hidden"
            }
          },
          "& #actionPlanDialog": {
            padding: "0",
            "& .MuiTabs-root": {
              backgroundColor: tokens.Primary.Primary200,
              padding: "0px 24px"
            },
            "& .MuiTabPanel-root": {
              padding: "0px 24px"
            },
            "& > .MuiBox-root": {
              height: "100%",
              backgroundColor: "unset",
              "& > .MuiBox-root": {
                height: "49px",
                backgroundColor: tokens.Primary.Primary200,
                marginBottom: 0
              },
              "& div[role='tabpanel']": {
                backgroundColor: tokens.Background.General.Primary,
                maxHeight: "315px",
                overflow: "auto",
                "& > .MuiGrid-root": {
                  paddingBottom: 0
                }
              }
            }
          },
          "& #alert-dialog-title": {
            // minHeight: "24%",
            boxSizing: "border-box"
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          width: "93%"
        },
        colorPrimary: {
          backgroundColor: tokens.Background.General.Secondary,
          color: colors.primaryColor
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "16px",
          fontWeight: "600",
          lineHeight: "22px",
          fontFamily: "Noto Sans",
          color: colors.Neutral.Neutral400,
          "&:hover": {
            opacity: "0.5"
          }
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: "#F7F8F9"
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          border: "none",
          backgroundColor: colors.Blue.Blue100,
          color: colors.Blue.Blue500
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.Neutral.Neutral800,
          color: colors.Neutral.Neutral0,
          padding: "8px 16px",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: "18px",
          "& .MuiTooltip-arrow": {
            color: colors.Neutral.Neutral800
          }
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: "2px",
          borderRadius: "100px",
          backgroundColor: colors.Neutral.Neutral200
        },
        barColorPrimary: {
          backgroundColor: colors.Cyan.Cyan400
        }
      }
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: tokens.Primary.Primary400,
          "&.Mui-active": {
            color: tokens.Primary.Primary400
          },
          "&.Mui-completed": {
            color: tokens.Primary.Primary400
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            minHeight: "43px",
            boxSizing: "border-box",
            borderColor: tokens.Border.General.Tertiary,
            "& #outlined-basic": {
              padding: "4px 0 4px 4px"
            },

            "&:hover ": {
              borderColor: tokens.Border.General.Primary
            },
            "&:focus ": {
              borderColor: tokens.Border.General.LightColor
            }
          }
        }
      }
    },
    ...muiSwitchStyle.components
  }
});
